import { type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

export interface SectionCardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  /** Disable inner padding — for content that brings its own (e.g., Table). */
  padded?: boolean;
  /** Dense inner padding (16px). Default true. */
  dense?: boolean;
  children: ReactNode;
}

export const SectionCard = ({
  title,
  description,
  aside,
  padded = true,
  dense = true,
  children,
  className,
  style,
  ...props
}: SectionCardProps) => {
  const hasHeader = Boolean(title || aside || description);
  const innerPadClass = padded ? (dense ? 'p-4' : 'p-6') : '';
  const headerPadClass = dense ? 'px-4 py-3' : 'px-6 py-4';
  return (
    <section
      className={cn('rounded-xl overflow-hidden', className)}
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--divider-strong)',
        boxShadow: 'var(--shadow-card-rest)',
        ...style,
      }}
      {...props}
    >
      {hasHeader ? (
        <div
          className={cn('flex items-start justify-between gap-3', headerPadClass)}
          style={{ borderBottom: '1px solid var(--divider-soft)' }}
        >
          <div className="flex min-w-0 flex-col gap-0.5">
            {title ? (
              <h3
                className="text-sm font-semibold uppercase tracking-wide text-[color:var(--content-strong)]"
                style={{ lineHeight: 'var(--leading-heading)' }}
              >
                {title}
              </h3>
            ) : null}
            {description ? (
              <p className="text-[12px] normal-case" style={{ color: 'var(--content-muted)' }}>
                {description}
              </p>
            ) : null}
          </div>
          {aside ? <div className="flex shrink-0 items-center gap-2">{aside}</div> : null}
        </div>
      ) : null}
      <div className={innerPadClass}>{children}</div>
    </section>
  );
};
